import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { createZodFormResolver, useFormControls } from '@mews/framework';
import {
    LearningPath,
    useInvalidateRepositoryQuery,
    useRepositoryMutation,
    UserGroup,
    UserGroupAccessPolicyType,
    UserGroupUpdateParameters,
} from '@mews-commander/data-access';

const baseSchema = z.object({
    enterprises: z.array(z.string()).optional().nullable(),
    description: z.string().max(1000).optional().nullable(),
    accessPolicyType: z.nativeEnum(UserGroupAccessPolicyType),
    cashierExternalIdentifiers: z.array(z.string()).optional().nullable(),
    assignedLearningPath: z.nativeEnum(LearningPath).optional().nullable().or(z.literal('')),
});

const groupSettingsSchema = z.discriminatedUnion('accessPolicyType', [
    baseSchema.extend({
        accessPolicyType: z.literal(UserGroupAccessPolicyType.RoleBased),
        roleIds: z.array(z.string()).nonempty(),
        receiveCustomerMessages: z.boolean().optional(),
    }),
    baseSchema.extend({
        accessPolicyType: z.literal(UserGroupAccessPolicyType.Admin),
        receiveCustomerMessages: z.boolean().optional(),
        resetUsersTwoFactorAuthentication: z.boolean().optional(),
    }),
    baseSchema.extend({
        accessPolicyType: z.literal(UserGroupAccessPolicyType.NoAccess),
    }),
]);

export type UpdateGroupSettingsFormValues = z.infer<typeof groupSettingsSchema>;

export const useGroupSettingsForm = ({ group, onSuccess }: { group: UserGroup; onSuccess: () => void }) => {
    const invalidate = useInvalidateRepositoryQuery();

    const { mutate: updateGroups, isPending } = useRepositoryMutation('userGroups', 'update', {
        onSuccess: () => {
            void invalidate('userGroups', 'getAll');
            void invalidate('userGroups', 'get', { userGroupId: group.id });
            onSuccess();
        },
    });

    const resolver = createZodFormResolver<UpdateGroupSettingsFormValues>(groupSettingsSchema);

    const defaultValues = useMemo(
        () => ({
            enterprises: group.enterprises?.map((p) => p.id),
            description: group.description,
            assignedLearningPath: group.assignedLearningPath,
            accessPolicyType: group.accessPolicy?.accessPolicyType,
            roleIds: group.accessPolicy?.roles?.map((role) => role.id) || [],
            cashierExternalIdentifiers: group.cashierExternalIdentifiers || [],
            receiveCustomerMessages: group.accessPolicy?.enterpriseProfileOptions?.receiveCustomerMessages,
            resetUsersTwoFactorAuthentication:
                group.accessPolicy?.enterpriseProfileOptions?.resetUsersTwoFactorAuthentication,
        }),
        [group]
    ) as UpdateGroupSettingsFormValues;

    const {
        renderFormContent,
        submit,
        watch,
        hasDirtyFields,
        reset: internalReset,
    } = useFormControls({
        resolver,
        defaultValues,
        mode: 'onChange',
        onSubmit: (values) => {
            updateGroups({
                userGroupId: group.id,
                ...parseSubmittedValues(values),
            });
        },
        shouldFocusError: true,
    });

    // Since the form is not being mounted/unmounted, we need to reset the form when the default values change
    useEffect(() => {
        internalReset(defaultValues);
    }, [internalReset, defaultValues]);

    return {
        renderFormContent,
        isPending,
        submit,
        watch,
        hasDirtyFields,
        reset: () => internalReset(defaultValues),
    };
};

const parseSubmittedValues = (
    values: UpdateGroupSettingsFormValues
): Omit<UserGroupUpdateParameters, 'userGroupId'> => {
    const hasEnterpriseProfileOptions =
        values.accessPolicyType !== UserGroupAccessPolicyType.NoAccess &&
        (values.receiveCustomerMessages ||
            (values.accessPolicyType === UserGroupAccessPolicyType.Admin && values.resetUsersTwoFactorAuthentication));

    return {
        ...(values.description && { description: { value: values.description } }),
        ...(values.enterprises && { enterpriseIds: { value: values.enterprises } }),
        ...((values.accessPolicyType || hasEnterpriseProfileOptions) && {
            accessPolicy: {
                value: {
                    accessPolicyType: values.accessPolicyType,
                    ...(values.accessPolicyType === UserGroupAccessPolicyType.RoleBased && {
                        roleIds: values.roleIds,
                    }),
                    ...(hasEnterpriseProfileOptions && {
                        enterpriseProfileOptions: {
                            receiveCustomerMessages: values.receiveCustomerMessages,
                            resetUsersTwoFactorAuthentication:
                                values.accessPolicyType === UserGroupAccessPolicyType.Admin
                                    ? values.resetUsersTwoFactorAuthentication
                                    : false,
                        },
                    }),
                },
            },
        }),
        ...(values.cashierExternalIdentifiers && {
            cashierExternalIdentifiers: { value: values.cashierExternalIdentifiers },
        }),
        ...(typeof values.assignedLearningPath === 'string' && {
            assignedLearningPath: { value: values.assignedLearningPath || null },
        }),
    };
};
